import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { FaqList } from "@/components/site/FaqList";
import { TextField, SelectField, AreaField, readForm } from "@/components/site/FormFields";
import { breadcrumbScript } from "@/lib/schema";
import { submitInquiry } from "@/lib/submit-inquiry";

const URL = "https://avoluscars.com/rent-out-your-car";
const TITLE = "Consign Your Luxury Car in Vancouver | Avolus Cars";
const DESC =
  "Own a luxury car in Vancouver or Richmond? See if it's a fit for the Avolus fleet. Submit your vehicle for a quick review.";

const ownerFaqs = [
  {
    q: "Who handles insurance?",
    a: "Insurance arrangements depend on the vehicle and how it joins the fleet. This is covered in detail during the review call, before anything is agreed.",
  },
  {
    q: "How are earnings split?",
    a: "Earnings depend on the vehicle, its availability and the arrangement we agree on together. This is covered in detail during the review call.",
  },
  {
    q: "Can I block dates for personal use?",
    a: "Availability is something we plan with you. How personal-use dates work for your car is covered in detail during the review call.",
  },
  {
    q: "How are renters screened?",
    a: "Every renter goes through the same checks we use for our own fleet, including our driver eligibility requirements. The full process is covered in detail during the review call.",
  },
  {
    q: "What happens if there's damage?",
    a: "We have a clear process for inspections and damage. How it applies to your vehicle is covered in detail during the review call, so you know exactly where you stand.",
  },
];

export const Route = createFileRoute("/rent-out-your-car")({
  component: RentOutPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: ownerFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Consign", url: URL },
      ]),
    ],
  }),
});

const steps = [
  { n: "01", title: "Submit your car", body: "Share the make, model, year, kilometres and when it's usually free. It takes about two minutes." },
  { n: "02", title: "We review fit", body: "We look at the car, its condition and how it would suit our clients and the current fleet." },
  { n: "03", title: "Discuss terms", body: "If it's a good match, we set up a call to walk through how everything works and answer your questions." },
  { n: "04", title: "Your car joins the fleet", body: "Once we've agreed, we prepare the car and start offering it to Avolus clients." },
];

const handled = [
  { title: "Bookings", body: "We take the enquiries, confirm the dates and manage the calendar." },
  { title: "Guest screening", body: "Every renter is checked against the same eligibility rules we use for our own cars." },
  { title: "Delivery", body: "We deliver the car to the client and collect it afterwards. You don't meet strangers in parking lots." },
  { title: "Cleaning", body: "The car is detailed between rentals so it's always presented properly." },
  { title: "Guest communication", body: "Questions, directions, timing changes: our concierge handles it all." },
];

function RentOutPage() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const get = readForm(e.currentTarget);
    const name = get("name");
    const email = get("email");
    const phone = get("phone");
    const make = get("make");
    const model = get("model");
    const year = get("year");
    const km = get("kilometres");
    if (!name || !email || !phone || !make || !model || !year || !km)
      return toast.error("Please fill in all required fields.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email.");

    const message = [
      "VEHICLE SUBMISSION",
      `Vehicle: ${year} ${make} ${model}`,
      `Kilometres: ${km}`,
      `City: ${get("city") || "Not provided"}`,
      `Availability: ${get("availability") || "Not provided"}`,
      `Condition: ${get("condition") || "Not provided"}`,
      "",
      `Notes: ${get("notes") || "Not provided"}`,
    ]
      .join("\n")
      .slice(0, 2000);

    setSubmitting(true);
    const { error } = await submitInquiry({
      name: name.slice(0, 100),
      email,
      phone: phone.slice(0, 30),
      message,
      formType: "consign",
      subject: `Vehicle Submission: ${year} ${make} ${model}`.slice(0, 200),
    });
    setSubmitting(false);
    if (error) return toast.error("Something went wrong. Please try again or call us.");
    setDone(true);
  };

  return (
    <Layout inner>
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Consign</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground max-w-4xl leading-[1.05]">
              Consign Your Luxury Car With Avolus
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Earn from your car while it sits idle. If you own a luxury or performance car in
              Vancouver or Richmond, it could join the Avolus fleet, and we handle the work that
              usually makes renting out a car a hassle.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#qualify"
                className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
              >
                Check If Your Car Qualifies
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Why Owners Talk to Us</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              A great car shouldn't spend most of its life parked.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Plenty of beautiful cars in Vancouver spend the week in a parkade. Maybe you
                travel for work, maybe it's a second car, or maybe winter means it barely moves.
                Meanwhile, our clients are looking for exactly that kind of vehicle for weddings,
                business trips, family visits and weekends up the Sea-to-Sky.
              </p>
              <p>
                Avolus connects the two. We're a delivery-first luxury car rental, which means
                our clients never pick up from a lot. We bring the car to them, look after them
                during the rental, and bring it back. That same care applies to every car in the
                fleet, including yours.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border bg-muted/40">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-12">
              From your driveway to our fleet.
            </h2>
            <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {steps.map((s) => (
                <li key={s.n} className="border border-border bg-card p-8">
                  <div className="text-xs tracking-[0.3em] text-muted-foreground">{s.n}</div>
                  <h3 className="mt-4 font-display text-2xl text-foreground">{s.title}</h3>
                  <p className="mt-3 text-foreground/80 leading-relaxed">{s.body}</p>
                </li>
              ))}
            </ol>
            <p className="mt-10 text-foreground/80 leading-relaxed max-w-3xl">
              Submitting your car doesn't commit you to anything. It simply starts the
              conversation, and you decide whether to go ahead once you've heard how it all works.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>What We Typically Look For</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              Cars our clients ask for.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                We typically look for luxury and performance vehicles that match the feel of
                our current{" "}
                <Link to="/fleet" className="underline underline-offset-4 hover:text-foreground">
                  fleet
                </Link>
                . That usually means cars like:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>BMW M and M Performance models</li>
                <li>Porsche</li>
                <li>Mercedes-AMG and the Mercedes-Benz S-Class</li>
                <li>Range Rover</li>
                <li>Cadillac Escalade</li>
                <li>Similar luxury SUVs, sedans and coupes</li>
              </ul>
              <p>
                Condition and presentation matter as much as the badge. A well-kept car that
                looks great in person is what makes a client's day. If your car isn't on the list
                but you think it belongs, send it anyway. We review every submission.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Avolus vs Peer-to-Peer Apps</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              You own the car. We do the work.
            </h2>
            <p className="text-foreground/80 leading-relaxed max-w-3xl mb-10">
              Listing a car on a peer-to-peer app means you become the rental company: answering
              messages at midnight, meeting strangers for handovers, cleaning between trips and
              hoping the next guest is a careful one. With Avolus, our team takes that on.
            </p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {handled.map((h) => (
                <div key={h.title} className="border border-border bg-card p-8">
                  <h3 className="font-display text-2xl text-foreground">{h.title}</h3>
                  <p className="mt-3 text-foreground/80 leading-relaxed">{h.body}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="qualify" className="py-20 lg:py-28 border-t border-border scroll-mt-28">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Fit Check</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-4">
              Check if your car qualifies.
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-10">
              Tell us about your car and we'll get back to you by email. We'll request photos by
              email.
            </p>
          </Reveal>
          {done ? (
            <div role="status" className="border border-border p-12 text-center bg-card">
              <h3 className="font-display text-3xl text-foreground mb-3">Submission received.</h3>
              <p className="text-foreground/70">
                Thank you. We'll review your car and email you shortly, including a request for
                photos.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="grid gap-6 md:grid-cols-2 border border-border p-8 md:p-10 bg-card"
            >
              <TextField id="name" label="Name" required maxLength={100} />
              <TextField id="email" label="Email" type="email" required maxLength={255} />
              <TextField id="phone" label="Phone" type="tel" required maxLength={30} />
              <TextField id="make" label="Make" required maxLength={60} />
              <TextField id="model" label="Model" required maxLength={80} />
              <TextField id="year" label="Year" required maxLength={4} inputMode="numeric" />
              <TextField id="kilometres" label="Kilometres" required maxLength={12} inputMode="numeric" />
              <SelectField
                id="city"
                label="City"
                options={["Vancouver", "Richmond", "Burnaby", "North or West Vancouver", "Other"]}
              />
              <SelectField
                id="availability"
                label="Availability"
                options={["Full-time", "Weekdays", "Weekends", "Seasonal"]}
              />
              <SelectField
                id="condition"
                label="Vehicle condition"
                options={["Mint", "Excellent", "Light wear"]}
              />
              <AreaField id="notes" label="Notes" />
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Submit My Car"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Owner FAQ</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-10">
              Owner questions, answered.
            </h2>
          </Reveal>
          <FaqList items={ownerFaqs} prefix="o" />
          <p className="mt-10 text-sm text-muted-foreground">
            Rather talk it through?{" "}
            <Link to="/contact" className="text-foreground underline underline-offset-4">
              Contact the concierge
            </Link>
            .
          </p>
        </div>
      </section>
    </Layout>
  );
}
